import { v4 as uuid } from 'uuid';

import { arrayBufferToBase64, base64ToArrayBuffer, stringToArrayBuffer } from '../../../utils';

type CryptoParams = {
  importKey: Parameters<SubtleCrypto['importKey']>[2];
  encrypt: Parameters<SubtleCrypto['encrypt']>[0];
};

/**
 * Maps Fiserv-supplied algorithms to Web Crypto API parameters.
 */
const KNOWN_ALGORITHMS: Record<string, CryptoParams> = {
  'RSA/None/OAEPWithSHA512AndMGF1Padding': {
    importKey: {
      name: 'RSA-OAEP',
      hash: {
        name: 'SHA-512',
      },
    },
    encrypt: {
      name: 'RSA-OAEP',
    },
  },
};

declare const ENCRYPTED_STRING: unique symbol;

/**
 * A basic implementation of a 'phantom' type that can be used to enforce compile-time checking that data has been
 * encrypted before transmission. However, at runtime, these are just plain strings.
 */
export type EncryptedString = string & { [ENCRYPTED_STRING]: true };

/**
 * Returns a function that may be used to encrypt plaintext strings using the specified encryption algorithm and public
 * key.
 *
 * `algorithm` must correspond to an entry in `KNOWN_ALGORITHMS`. If not, this function will throw an error.
 * `base64EncodedPublicKeyBody` must be a base-64 encoded string, with `BEGIN/END PUBLIC KEY` headers and footers
 * removed.
 */
export const encryptUsing = (algorithm: string, base64EncodedPublicKeyBody: string) => {
  if (!KNOWN_ALGORITHMS[algorithm]) {
    throw new Error(`Unknown algorithm '${algorithm}'`);
  }

  const { importKey, encrypt } = KNOWN_ALGORITHMS[algorithm];

  // The async. import of a public key only needs to be done once per key, so it can be performed outside of the
  // function and the result re-used where required.
  const importPublicKey = crypto.subtle.importKey(
    'spki',
    base64ToArrayBuffer(base64EncodedPublicKeyBody),
    importKey,
    false,
    ['encrypt'],
  );

  return (plaintext: string): Promise<EncryptedString> =>
    importPublicKey
      .then((publicKey) => crypto.subtle.encrypt(encrypt, publicKey, stringToArrayBuffer(plaintext)))
      .then(arrayBufferToBase64 as (buffer: ArrayBuffer) => EncryptedString);
};

/**
 * Describes the request body expected by the `POST /v1/account-tokens` endpoint of Fiserv's API
 */
export type IFiservCreateAccountTokenRequestBody = {
  fdCustomerId: string;
  referenceToken: { tokenType: string };
  account: {
    type: 'CREDIT'; // TODO: Support other card types?
    credit: {
      cardNumber: EncryptedString;
      // Upper case
      cardType: string;
      securityCode: EncryptedString;
      nameOnCard: string;
      expiryDate: {
        month: EncryptedString;
        year: EncryptedString;
      };
      billingAddress: {
        postalCode: string;
      };
    };
  };
};

export type IFiservCreateAccountTokenRequestOptions = {
  baseUrl: string;
  body: IFiservCreateAccountTokenRequestBody;
  apiKey: string;
  oAuthToken: string;
};

/**
 * Submits an HTTP request to the `/v1/account-tokens` endpoint of Fiserv's API, using the specified base URL, request
 * body, API Key and oAuth Token.
 *
 */
export const createToken = ({ baseUrl, body, apiKey, oAuthToken }: IFiservCreateAccountTokenRequestOptions) =>
  fetch(`${baseUrl}/v1/account-tokens`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Api-Key': apiKey,
      Authorization: `Bearer ${oAuthToken}`,
      'Client-Request-Id': uuid(),
      'Content-Type': 'application/json',
      Timestamp: Date.now().toString(),
    },
  });
